/** Each mounted child owns only its pending registration; old completions cannot clear siblings. */
export function createBookPendingGroup(
  report: (pending: boolean) => void,
  refresh: () => void,
  refreshLocal?: () => void
) {
  const pending = new Set<symbol>()
  let active = true
  let generation = 0
  let reported = false
  let refreshRequested = false
  let contextRefreshRequested = false
  let refreshScheduled = false
  const flushRefresh = () => {
    if (!active || pending.size || !refreshRequested || refreshScheduled) return
    const version = generation
    refreshScheduled = true
    queueMicrotask(() => {
      if (version !== generation) return
      refreshScheduled = false
      if (active && !pending.size && refreshRequested) {
        const refreshContext = contextRefreshRequested
        refreshRequested = false
        contextRefreshRequested = false
        if (!refreshContext && refreshLocal) refreshLocal()
        else refresh()
      }
    })
  }
  const publish = () => {
    const next = pending.size > 0
    if (next !== reported) {
      reported = next
      report(next)
    }
  }
  return {
    activate() {
      active = true
    },
    attach() {
      const token = Symbol('Book child')
      const version = generation
      let attached = true
      const current = () => active && attached && generation === version
      return {
        set(value: boolean) {
          if (!current()) return
          if (value) pending.add(token)
          else pending.delete(token)
          publish()
          flushRefresh()
        },
        release() {
          if (!current()) return
          attached = false
          pending.delete(token)
          // Removing a view is not a completed receipt and must not launch a queued refresh.
          if (!pending.size) {
            refreshRequested = false
            contextRefreshRequested = false
          }
          publish()
        },
      }
    },
    requestRefresh() {
      if (!active) return
      refreshRequested = true
      contextRefreshRequested = true
      flushRefresh()
    },
    requestLocalRefresh() {
      if (!active) return
      refreshRequested = true
      flushRefresh()
    },
    dispose() {
      active = false
      generation++
      pending.clear()
      refreshRequested = false
      contextRefreshRequested = false
      refreshScheduled = false
      publish()
    },
  }
}

/** Async results belong to one mounted review/session, including StrictMode effect restarts. */
export function createBookRequestScope() {
  let active = false
  let generation = 0
  return {
    mount() {
      active = true
    },
    begin() {
      const version = ++generation
      return { current: () => active && generation === version }
    },
    dispose() {
      active = false
      generation++
    },
  }
}
